import React, { useState, useEffect } from "react";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button, InputField } from "@cakrawalajakarta/shared-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faLocationDot,
  faChevronDown,
  faCar,
  faUser,
  faCheckCircle,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";

export const RSVPRPC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const db = getFirestore();
  const [selectedLocation, setSelectedLocation] = useState("");
  const [vehiclePlateNumber, setVehiclePlateNumber] = useState("");
  const [userData, setUserData] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  const [suratPernyataan, setSuratPernyataan] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Lokasi RPC
  const locations = [
    {
      name: "Decathlon Agora Mall",
      date: "23-24/09/2025",
      time: "12:00 - 20:00",
      mapLink:
        "https://www.google.com/maps/search/?api=1&query=Decathlon+Agora+Mall",
    },
    {
      name: "Balai Besar TNGGP",
      date: "26/09/2025",
      time: "14:00 - 20:00",
      mapLink:
        "https://www.google.com/maps/search/?api=1&query=Balai+Besar+TNGGP",
    },
  ];

  // Ambil data peserta dan surat pernyataan dari Firestore
  useEffect(() => {
    const fetchUserDataAndSurat = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Ambil data peserta dari pendaftaranUltra24
        const q = query(
          collection(db, "pendaftaranUltra24"),
          where("uid", "==", user.uid),
          where("transaction_status", "==", "settlement")
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          setOrderId(doc.id);
          setUserData({
            namaLengkap: data.namaLengkap || "Pengguna",
            kategori: data.kategori || "-",
            bibNumber: data.bibNumber || "-",
            sizeJersey: data.sizeJersey || "-",
            rpcLocation: data.rpcLocation || "",
            vehiclePlateNumber: data.vehiclePlateNumber || "",
          });
          setSelectedLocation(data.rpcLocation || "");
          setVehiclePlateNumber(data.vehiclePlateNumber || "");
          setHasSelected(!!data.rpcLocation);
        }

        // Ambil data surat pernyataan untuk event ULTRA24
        const suratQuery = query(
          collection(db, "suratPernyataan"),
          where("userId", "==", user.uid),
          where("eventId", "==", "ULTRA24")
        );
        const suratSnapshot = await getDocs(suratQuery);
        if (!suratSnapshot.empty) {
          let latestSurat = null;
          suratSnapshot.forEach((doc) => {
            const data = doc.data();
            if (
              !latestSurat ||
              (data.createdAt &&
                data.createdAt.seconds > latestSurat.createdAt?.seconds)
            ) {
              latestSurat = {
                downloadURL: data.downloadURL,
                fileName: data.fileName,
                namaLengkap: data.namaLengkap,
                createdAt: data.createdAt,
              };
            }
          });
          setSuratPernyataan(latestSurat);
        }
      } catch (err) {
        console.error("[ERROR] Mengambil data peserta atau surat:", err);
        showNotification(
          "error",
          "Gagal memuat data peserta atau surat pernyataan. Silakan coba lagi."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserDataAndSurat();
  }, [user, db, showNotification]);

  // Handle pemilihan lokasi
  const handleLocationSelect = (e) => {
    setSelectedLocation(e.target.value);
  };

  // Handle input plat nomor
  const handleVehiclePlateNumberChange = (e) => {
    setVehiclePlateNumber(e.target.value.toUpperCase());
  };

  // Handle submit pilihan lokasi dan plat nomor
  const handleSubmit = async () => {
    if (!user?.uid) {
      showNotification(
        "error",
        "Silakan login untuk memilih lokasi dan mendaftarkan plat nomor kendaraan."
      );
      navigate("/login");
      return;
    }

    if (!userData) {
      showNotification(
        "error",
        "Data pendaftaran tidak ditemukan. Silakan hubungi panitia."
      );
      return;
    }

    if (!selectedLocation) {
      showNotification(
        "error",
        "Silakan pilih salah satu lokasi pengambilan race pack."
      );
      return;
    }

    setSubmitting(true);
    try {
      const orderRef = doc(db, "pendaftaranUltra24", orderId);
      const updateData = {
        rpcLocation: selectedLocation,
        updatedAt: new Date(),
      };
      if (vehiclePlateNumber.trim()) {
        updateData.vehiclePlateNumber = vehiclePlateNumber.trim();
      }
      await updateDoc(orderRef, updateData);
      setHasSelected(true);
      showNotification(
        "success",
        `Pilihan lokasi "${selectedLocation}"${
          vehiclePlateNumber.trim()
            ? ` dan plat nomor "${vehiclePlateNumber}"`
            : ""
        } berhasil disimpan!`
      );
    } catch (err) {
      console.error("[ERROR] Menyimpan pilihan lokasi dan plat nomor:", err);
      showNotification(
        "error",
        "Gagal menyimpan pilihan lokasi dan plat nomor. Silakan coba lagi."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Fungsi untuk toggle tampilan detail
  const handleShowDetails = () => {
    setShowDetails(true);
  };

  // Fungsi untuk kembali ke form view
  const handleBackToForm = () => {
    setShowDetails(false);
  };

  // Fungsi untuk mengedit pilihan
  const handleEditSelection = () => {
    setHasSelected(false);
    setShowDetails(false);
  };

  // Fungsi untuk memformat tanggal
  const formatDate = (dateString) => {
    if (!dateString || dateString === "To Be Confirmed") return dateString;
    try {
      if (dateString.includes("-")) {
        const [range, month, year] = dateString.split("/");
        return `${range} Sep ${year}`;
      }
      const [day, month, year] = dateString.split("/");
      const date = new Date(`${year}-${month}-${day}`);
      if (isNaN(date)) return "-";
      return date.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  // Tampilan detail pilihan
  const renderSelectedView = () => {
    const selectedLocationData = locations.find(
      (loc) => loc.name === selectedLocation
    );

    return (
      <section className="min-h-screen bg-white flex items-center justify-center px-4 py-20">
        <div className="w-full max-w-[450px]">
          <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden">
            {/* Header Section */}
            <div className="text-center">
              <FontAwesomeIcon
                icon={faBoxOpen}
                className="h-12 w-12 text-red-600 my-4"
              />
              <h2 className="text-xl font-bold text-red-600">
                ULTRA24
              </h2>
              <p className="text-lg font-medium text-red-600">
                Racepack Collection
              </p>
              <div className="mt-1 text-gray-600 text-sm">
                <span>{selectedLocation || "Memuat data..."}</span>
              </div>
            </div>

            {/* Status Section */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-red-600">
                  Detail RPC
                </h3>
                {vehiclePlateNumber.trim() && (
                  <span className="flex items-center gap-2 text-sm font-medium px-3 py-1 rounded-full border border-red-600 text-red-700">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-red-600"
                    />
                    Gratis Parkir
                  </span>
                )}
              </div>
              <div className="mt-2 space-y-2 text-gray-600 text-sm">
                <p>
                  <span className="font-bold text-gray-800">Lokasi:</span>{" "}
                  {selectedLocation || "-"}
                </p>
                <p>
                  <span className="font-bold text-gray-800">Tanggal:</span>{" "}
                  {selectedLocationData
                    ? formatDate(selectedLocationData.date)
                    : "-"}
                </p>
                <p>
                  <span className="font-bold text-gray-800">Waktu:</span>{" "}
                  {selectedLocationData?.time || "-"}
                </p>
              </div>
            </div>

            {/* Detail Pendaftar Section */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-red-600 mb-4">
                Detail Pendaftar
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    icon: faUser,
                    label: "Nama",
                    value: userData?.namaLengkap || "Memuat data...",
                  },
                  {
                    icon: faUser,
                    label: "Kategori Lomba",
                    value: userData?.kategori || "Memuat data...",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="p-3 bg-red-50 border border-red-200 rounded-xl"
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="h-4 w-4 text-red-600"
                      />
                      <span className="text-sm font-bold text-red-600">
                        {item.label}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 ml-6">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gratis Parkir Section */}
            {vehiclePlateNumber.trim() && (
              <div className="mt-4 flex justify-center">
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl max-w-md w-full text-center">
                  <div className="flex justify-center items-center space-x-2">
                    <p className="text-lg font-semibold text-red-600">
                      Gratis Parkir
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">Nomor Kendaraan</p>
                  <div className="text-4xl uppercase font-black text-red-600">
                    {vehiclePlateNumber}
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    Tunjukkan halaman ini saat memasuki area TNGGP
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  // Tampilan formulir pemilihan lokasi
  const renderFormView = () => (
    <section
      id="rsvp-rpc"
      className="w-full min-h-screen py-20 text-center text-red-600 flex-col justify-center relative bg-white"
    >
      <div className="relative z-0 container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-6xl mx-auto space-y-4">
            {/* RPC Info - Statis */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              {locations.map((location, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl px-6 py-8 md:py-12 border border-gray-200 flex flex-col items-center justify-center"
                >
                  <FontAwesomeIcon
                    icon={faBoxOpen}
                    className="text-4xl text-red-600 mb-4"
                  />
                  <a
                    href={location.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-red-600 font-bold mb-3 hover:underline flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faLocationDot} />
                    Lokasi RPC {index + 1}
                  </a>
                  <p className="text-xl text-red-800">{location.name}</p>
                  <p className="text-sm text-gray-600">
                    Tanggal: {formatDate(location.date)}{" "}
                    {location.time ? `(${location.time})` : ""}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* Keterangan */}
            <motion.div
              className="bg-white rounded-2xl border border-gray-200 px-6 py-6 md:px-8 md:py-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg md:text-xl font-bold text-red-600 mb-4">
                Informasi Race Pack Collection
              </h4>
              <p className="text-base text-gray-600 max-w-5xl mx-auto text-center">
                Tidak ada pengecekan Mandatory Gears saat pengambilan racepack,
                pengecekan akan dilakukan secara acak di rute perlombaan!
                Pengambilan racepack dapat dilakukan di salah satu lokasi yang
                tersedia: Decathlon Agora Mall atau Balai Besar TNGGP. Pastikan
                Anda memilih lokasi yang sesuai. Harap membawa identitas diri,
                surat keterangan sehat (berlaku 7-hari sebelum jadwal
                perlombaan) dan{" "}
                {suratPernyataan ? (
                  <a
                    href={suratPernyataan.downloadURL}
                    download={suratPernyataan.fileName}
                    className="text-red-600 no-underline italic hover:underline"
                    aria-label="Unduh surat pernyataan"
                  >
                    Surat Pernyataan
                  </a>
                ) : (
                  <span className="text-gray-500 italic">
                    Surat Pernyataan (belum tersedia)
                  </span>
                )}{" "}
                pada saat pengambilan race pack. Jika diwakilkan harap membawa{" "}
                <a
                  href="https://firebasestorage.googleapis.com/v0/b/cakrawala-jakartakreatif.firebasestorage.app/o/suratKuasa%2FSURAT%20KUASA.pdf?alt=media&token=ae20209d-58c1-4131-938c-1c0b1d9d3edc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 no-underline italic hover:underline"
                >
                  Surat Alih Kuasa
                </a>
                . Untuk peserta Mix Group, racepack dapat diambil oleh salah
                satu perwakilan dengan membawa surat pernyataan dan surat
                keterangan sehat seluruh peserta.
              </p>
            </motion.div>

            {/* Container 1: Informasi Peserta */}
            <motion.div
              className="bg-white rounded-2xl border border-gray-200 px-4 py-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg md:text-xl font-bold text-red-600 mb-4">
                Informasi Peserta
              </h4>
              {loading ? (
                <p className="text-base text-gray-600">
                  Memuat data peserta...
                </p>
              ) : user && userData ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <p className="text-sm text-red-800 font-bold">
                      Nama Lengkap:
                    </p>
                    <p className="text-base text-red-600">
                      {userData.namaLengkap}
                    </p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <p className="text-sm text-red-800 font-bold">
                      Kategori Lomba:
                    </p>
                    <p className="text-base text-red-600">
                      {userData.kategori}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-base text-gray-600">
                  Silakan login untuk melihat informasi pendaftaran Anda,
                  memilih lokasi, dan mendaftarkan plat nomor kendaraan.
                </p>
              )}
            </motion.div>

            {/* Grid untuk dua container terpisah */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Container 2: Pemilihan Lokasi */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-200 px-4 py-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <h4 className="text-lg md:text-xl font-bold text-red-600 mb-4">
                  Pilih Lokasi Pengambilan
                </h4>
                <p className="text-base text-gray-600 mb-6">
                  Apabila peserta tidak memilih lokasi, pengambilan race pack
                  akan ditetapkan di Balai Besar TNGGP.
                </p>
                <div className="mb-6">
                  <InputField
                    type="select"
                    value={selectedLocation}
                    onChange={handleLocationSelect}
                    disabled={
                      loading ||
                      submitting ||
                      hasSelected ||
                      !user ||
                      !userData
                    }
                    options={[
                      { value: "", label: "-- Pilih Lokasi --" },
                      {
                        value: "Decathlon Agora Mall",
                        label: "Decathlon Agora Mall (23-24/09/2025, 12:00 - 20:00)",
                      },
                      {
                        value: "Balai Besar TNGGP",
                        label: "Balai Besar TNGGP (26/09/2025, 14:00 - 20:00)",
                      },
                    ]}
                  />
                </div>
                {hasSelected && (
                  <Button
                    onClick={handleEditSelection}
                    className="w-full"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Ubah Pilihan Lokasi
                  </Button>
                )}
              </motion.div>

              {/* Container 3: Input Plat Nomor dan Submit */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-200 px-4 py-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <h4 className="text-lg md:text-xl font-bold text-red-600 mb-4">
                  Gratis Parkir
                </h4>
                <p className="text-base text-gray-600 mb-6">
                  Masukkan nomor kendaraan Anda (opsional) untuk mendapatkan
                  akses parkir gratis di venue perlombaan pada hari-H.
                </p>
                <div className="mb-6">
                  <InputField
                    type="text"
                    value={vehiclePlateNumber}
                    onChange={handleVehiclePlateNumberChange}
                    placeholder="Contoh: B 1234 XYZ (atau kosongkan jika tidak membawa kendaraan)"
                    disabled={
                      loading ||
                      submitting ||
                      hasSelected ||
                      !user ||
                      !userData
                    }
                    startIcon={
                      <FontAwesomeIcon
                        icon={faCar}
                        className="text-gray-400"
                      />
                    }
                  />
                </div>
                <Button
                  onClick={hasSelected ? handleShowDetails : handleSubmit}
                  loading={loading || submitting}
                  disabled={
                    !hasSelected && (!selectedLocation || !user || !userData)
                  }
                  className="w-full"
                >
                  {hasSelected ? "Lihat Detail Pilihan" : "Simpan Pilihan"}
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <AnimatePresence mode="wait">
      {showDetails && hasSelected && user && userData
        ? renderSelectedView()
        : renderFormView()}
    </AnimatePresence>
  );
};
